import {useState, useCallback, useMemo, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  isFreeKickActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {ROUTES} from '@/utils/constants/routesConstants';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {
  setActivityFormData,
  resetActivityData
} from '@/store/slices/activitySlice';
import {getFileVerification} from '@/services/fileUploadService';
import {
  Constants,
  maxImageSizeInBytes
} from '@/utils/constants/commonConstants';
import {
  buildWebpImageUploadFormData,
  extractCdnUrlFromUploadResponse
} from '@/helpers/imageUploadForm';
import {useAppSelector} from '@/store';
import {getFreeKickGames} from '@/services/activities';

const EMPTY_LANG_DATA = {
  title: '',
  tagDescription: '',
  gameDescription: '',
  howToPlayRule: '',
  tutorialLine1: '',
  tutorialLine2: ''
};

const EMPTY_GAME_DISPLAY_DATA = {
  gameImageFile: null,
  gameImageUri: '',
  logoImageFile: null,
  logoImageUri: '',
  firstTeamName: '',
  secondTeamName: '',
  firstTeamImageFile: null,
  firstTeamImageUri: '',
  secondTeamImageFile: null,
  secondTeamImageUri: '',
  backgroundMenuFile: null,
  backgroundMenuUri: '',
  backgroundSelectTeamFile: null,
  backgroundSelectTeamUri: '',
  backgroundImageFile: null,
  backgroundImageUri: '',
  backgroundNextLevelFile: null,
  backgroundNextLevelUri: '',
  missImageFile: null,
  missImageUri: '',
  outImageFile: null,
  outImageUri: '',
  gameOverImageFile: null,
  gameOverImageUri: '',
  goalImageFile: null,
  goalImageUri: '',
  highBarImageFile: null,
  highBarImageUri: '',
  rightBarImageFile: null,
  rightBarImageUri: '',
  backgroundVSFile: null,
  backgroundVSUri: '',
  backgroundWaitingFile: null,
  backgroundWaitingUri: '',
  backgroundExitFile: null,
  backgroundExitUri: ''
};

const MAX_IMAGE_SIZE_IN_KB = Math.floor(maxImageSizeInBytes / 1024);
const IMAGE_FILE_ERROR_DEFAULT = 'Please use a valid PNG/JPG or WebP file';

const useFreeKickGameDisplay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countryCode = useAppSelector(selectCountryCode);
  const formData = useAppSelector((state) => state.activity.formData);

  const languages = useMemo(
    () =>
      COUNTRY_LANGUAGES_CODES[countryCode] || [
        {code: 'en', title: 'English', languageId: 1}
      ],
    [countryCode]
  );

  const existingTranslations = formData?.translations;
  const existingAsset = formData?.displayConfig?.asset;

  const buildInitialLangData = useCallback(() => {
    const data = {};

    languages.forEach((lang) => {
      const trans = existingTranslations?.[lang.code];

      if (trans) {
        data[lang.code] = {
          ...EMPTY_LANG_DATA,
          title: trans?.title || '',
          tagDescription: trans?.tagDescription || '',
          gameDescription: trans?.gameDescription || '',
          howToPlayRule: trans?.howToPlayRule || '',
          tutorialLine1: trans?.descriptionLine1 || '',
          tutorialLine2: trans?.descriptionLine2 || ''
        };
      } else {
        data[lang.code] = {...EMPTY_LANG_DATA};
      }
    });
    return data;
  }, [languages, existingTranslations]);

  const buildInitialGameData = useCallback(() => {
    // Pick firstTeamName/secondTeamName from any available language entry
    const firstLangCode = languages[0]?.code;
    const fallbackTrans =
      existingTranslations?.[firstLangCode] ||
      (existingTranslations ? Object.values(existingTranslations)[0] : null);

    if (existingAsset || fallbackTrans) {
      return {
        ...EMPTY_GAME_DISPLAY_DATA,
        gameImageUri: existingAsset?.gameImage || '',
        logoImageUri: existingAsset?.logoImage || '',
        firstTeamName: fallbackTrans?.firstTeamName || '',
        secondTeamName: fallbackTrans?.secondTeamName || '',
        firstTeamImageUri: existingAsset?.firstTeamImage || '',
        secondTeamImageUri: existingAsset?.secondTeamImage || '',
        backgroundMenuUri: existingAsset?.backgroundMenuImage || '',
        backgroundSelectTeamUri: existingAsset?.backgroundSelectTeamImage || '',
        backgroundImageUri: existingAsset?.backgroundImage || '',
        backgroundNextLevelUri: existingAsset?.backgroundNextLevelImage || '',
        missImageUri: existingAsset?.missImage || '',
        outImageUri: existingAsset?.outImage || '',
        gameOverImageUri: existingAsset?.gameOverImage || '',
        goalImageUri: existingAsset?.goalImage || '',
        highBarImageUri: existingAsset?.highBarImage || '',
        rightBarImageUri: existingAsset?.rightBarImage || '',
        backgroundVSUri: existingAsset?.backgroundVS || '',
        backgroundWaitingUri: existingAsset?.backgroundWaiting || '',
        backgroundExitUri: existingAsset?.backgroundExit || ''
      };
    }
    return {...EMPTY_GAME_DISPLAY_DATA};
  }, [existingAsset, existingTranslations, languages]);

  const [langData, setLangData] = useState(buildInitialLangData);
  const [gameData, setGameData] = useState(buildInitialGameData);

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [tutorialTabIndex, setTutorialTabIndex] = useState(0);
  const [isCancel, setIsCancel] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [gameImageProgress, setGameImageProgress] = useState(0);
  const [logoImageProgress, setLogoImageProgress] = useState(0);
  const [firstTeamImageProgress, setFirstTeamImageProgress] = useState(0);
  const [secondTeamImageProgress, setSecondTeamImageProgress] = useState(0);
  const [backgroundMenuProgress, setBackgroundMenuProgress] = useState(0);
  const [backgroundSelectTeamProgress, setBackgroundSelectTeamProgress] =
    useState(0);
  const [backgroundImageProgress, setBackgroundImageProgress] = useState(0);
  const [backgroundNextLevelProgress, setBackgroundNextLevelProgress] =
    useState(0);
  const [missImageProgress, setMissImageProgress] = useState(0);
  const [outImageProgress, setOutImageProgress] = useState(0);
  const [gameOverImageProgress, setGameOverImageProgress] = useState(0);
  const [goalImageProgress, setGoalImageProgress] = useState(0);
  const [highBarImageProgress, setHighBarImageProgress] = useState(0);
  const [rightBarImageProgress, setRightBarImageProgress] = useState(0);
  const [backgroundVSProgress, setBackgroundVSProgress] = useState(0);
  const [backgroundWaitingProgress, setBackgroundWaitingProgress] = useState(0);
  const [backgroundExitProgress, setBackgroundExitProgress] = useState(0);

  // AC4: State for "Use Images from Previous Games" dropdown
  const [previousGames, setPreviousGames] = useState([]);
  const [selectedPreviousGame, setSelectedPreviousGame] = useState('');
  const [loadingPreviousGames, setLoadingPreviousGames] = useState(false);

  const activeLangCode = languages[activeTabIndex]?.code;
  const tutorialLangCode = languages[tutorialTabIndex]?.code;

  const currentLangData = langData[activeLangCode] || {...EMPTY_LANG_DATA};
  const tutorialLangData = langData[tutorialLangCode] || {...EMPTY_LANG_DATA};
  const [gameImageValidationError, setGameImageValidationError] = useState('');
  const [logoImageValidationError, setLogoImageValidationError] = useState('');
  const [teamImageValidationError, setTeamImageValidationError] = useState('');
  const [secondTeamImageValidationError, setSecondTeamImageValidationError] =
    useState('');
  const [backgroundMenuValidationError, setBackgroundMenuValidationError] =
    useState('');
  const [
    backgroundSelectTeamValidationError,
    setBackgroundSelectTeamValidationError
  ] = useState('');
  const [backgroundImageValidationError, setBackgroundImageValidationError] =
    useState('');
  const [
    backgroundNextLevelValidationError,
    setBackgroundNextLevelValidationError
  ] = useState('');
  const [missImageValidationError, setMissImageValidationError] = useState('');
  const [outImageValidationError, setOutImageValidationError] = useState('');
  const [gameOverImageValidationError, setGameOverImageValidationError] =
    useState('');
  const [goalImageValidationError, setGoalImageValidationError] = useState('');
  const [highBarImageValidationError, setHighBarImageValidationError] =
    useState('');
  const [rightBarImageValidationError, setRightBarImageValidationError] =
    useState('');
  const [backgroundVSValidationError, setBackgroundVSValidationError] =
    useState('');
  const [
    backgroundWaitingValidationError,
    setBackgroundWaitingValidationError
  ] = useState('');
  const [backgroundExitValidationError, setBackgroundExitValidationError] =
    useState('');

  const getFileFromUploadInput = useCallback((fileOrFiles) => {
    if (!fileOrFiles) return null;
    if (fileOrFiles instanceof Blob) return fileOrFiles;
    if (Array.isArray(fileOrFiles)) return fileOrFiles[0] || null;
    if (fileOrFiles?.file instanceof Blob) return fileOrFiles.file;
    if (Array.isArray(fileOrFiles?.files)) return fileOrFiles.files[0] || null;
    if (fileOrFiles?.files?.[0]) return fileOrFiles.files[0];
    return fileOrFiles?.[0] || null;
  }, []);

  const validateImageFile = useCallback(async (file) => {
    if (file.name) {
      const normalizedName = file.name.toLowerCase();
      const isValidImageFormat =
        normalizedName.endsWith('.webp') ||
        normalizedName.endsWith('.jpg') ||
        normalizedName.endsWith('.jpeg') ||
        normalizedName.endsWith('.png');

      if (!isValidImageFormat) {
        throw new Error(IMAGE_FILE_ERROR_DEFAULT);
      }
    }

    if (file.size > maxImageSizeInBytes) {
      throw new Error(
        `Image size exceeds ${MAX_IMAGE_SIZE_IN_KB} KB. Please upload a smaller image.`
      );
    }
  }, []);

  const updateLangField = useCallback(
    (field, value) => {
      setLangData((prev) => {
        if (prev[activeLangCode]?.[field] === value) return prev;
        return {
          ...prev,
          [activeLangCode]: {
            ...prev[activeLangCode],
            [field]: value
          }
        };
      });
    },
    [activeLangCode]
  );

  const updateTutorialField = useCallback(
    (field, value) => {
      setLangData((prev) => {
        if (prev[tutorialLangCode]?.[field] === value) return prev;
        return {
          ...prev,
          [tutorialLangCode]: {
            ...prev[tutorialLangCode],
            [field]: value
          }
        };
      });
    },
    [tutorialLangCode]
  );

  const updateGameField = useCallback((field, value) => {
    setGameData((prev) => {
      if (prev[field] === value) return prev;
      return {...prev, [field]: value};
    });
  }, []);

  // AC4: Fetch previous Free Kick games for dropdown
  useEffect(() => {
    if (
      !isFreeKickActivityType(formData?.activityType) &&
      !isBackgammonActivityType(formData?.activityType)
    ) {
      return;
    }

    const fetchPreviousGames = async () => {
      setLoadingPreviousGames(true);
      try {
        const response = await getFreeKickGames({countryCode});
        if (response?.success && response?.data) {
          setPreviousGames(response.data);
        }
      } catch (error) {
        console.error('Error fetching previous games:', error);
      } finally {
        setLoadingPreviousGames(false);
      }
    };

    fetchPreviousGames();
  }, [countryCode, formData?.activityType]);

  // AC4: Handler to populate images from selected previous game
  const handlePreviousGameSelect = useCallback(
    (gameId) => {
      setSelectedPreviousGame(gameId);

      if (!gameId) return;

      const selectedGame = previousGames.find(
        (game) => game.id === gameId || game.uuid === gameId
      );

      if (selectedGame?.displayConfig?.asset) {
        const asset = selectedGame.displayConfig.asset;
        setGameData((prev) => ({
          ...prev,
          gameImageUri: asset.gameImage || prev.gameImageUri,
          logoImageUri: asset.logoImage || prev.logoImageUri,
          firstTeamImageUri: asset.firstTeamImage || prev.firstTeamImageUri,
          secondTeamImageUri: asset.secondTeamImage || prev.secondTeamImageUri,
          backgroundMenuUri:
            asset.backgroundMenuImage || prev.backgroundMenuUri,
          backgroundSelectTeamUri:
            asset.backgroundSelectTeamImage || prev.backgroundSelectTeamUri,
          backgroundImageUri: asset.backgroundImage || prev.backgroundImageUri,
          backgroundNextLevelUri:
            asset.backgroundNextLevelImage || prev.backgroundNextLevelUri,
          missImageUri: asset.missImage || prev.missImageUri,
          outImageUri: asset.outImage || prev.outImageUri,
          gameOverImageUri: asset.gameOverImage || prev.gameOverImageUri,
          goalImageUri: asset.goalImage || prev.goalImageUri,
          highBarImageUri: asset.highBarImage || prev.highBarImageUri,
          rightBarImageUri: asset.rightBarImage || prev.rightBarImageUri,
          backgroundVSUri: asset.backgroundVS || prev.backgroundVSUri,
          backgroundWaitingUri:
            asset.backgroundWaiting || prev.backgroundWaitingUri,
          backgroundExitUri: asset.backgroundExit || prev.backgroundExitUri
        }));
      }
    },
    [previousGames]
  );

  const onUploadProgress = (setProgress) => (progressEvent) => {
    const progress = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setProgress(progress === 100 ? 99 : progress);
  };

  const handleImageUpload = useCallback(
    async (
      fileOrFiles,
      fieldPrefix,
      setProgress,
      setValidationError = () => {}
    ) => {
      const file = getFileFromUploadInput(fileOrFiles);
      if (!file) return;

      setValidationError('');

      try {
        await validateImageFile(file);
      } catch (error) {
        setValidationError(
          error?.message || 'Failed to validate image. Please try again.'
        );
        return;
      }

      try {
        setProgress(0);
        const {formData} = await buildWebpImageUploadFormData(file);

        const response = await getFileVerification(
          formData,
          Constants.ActivityMedia,
          onUploadProgress(setProgress),
          'webp'
        );

        setProgress(100);

        const cdnUrl = extractCdnUrlFromUploadResponse(response);

        updateGameField(`${fieldPrefix}File`, file);
        updateGameField(`${fieldPrefix}Uri`, cdnUrl);
        setValidationError('');
      } catch (error) {
        setProgress(0);
        updateGameField(`${fieldPrefix}File`, null);
        updateGameField(`${fieldPrefix}Uri`, '');
        setValidationError(
          error?.message || 'Failed to upload image. Please try again.'
        );
      }
    },
    [getFileFromUploadInput, updateGameField, validateImageFile]
  );

  const handleGameImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'gameImage',
        setGameImageProgress,
        setGameImageValidationError
      ),
    [handleImageUpload]
  );

  const handleLogoImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'logoImage',
        setLogoImageProgress,
        setLogoImageValidationError
      ),
    [handleImageUpload]
  );

  const handleFirstTeamImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'firstTeamImage',
        setFirstTeamImageProgress,
        setTeamImageValidationError
      ),
    [handleImageUpload]
  );

  const handleSecondTeamImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'secondTeamImage',
        setSecondTeamImageProgress,
        setSecondTeamImageValidationError
      ),
    [handleImageUpload]
  );

  const handleGameImageDelete = useCallback(() => {
    updateGameField('gameImageFile', null);
    updateGameField('gameImageUri', '');
    setGameImageValidationError('');
  }, [updateGameField]);

  const handleLogoImageDelete = useCallback(() => {
    updateGameField('logoImageFile', null);
    updateGameField('logoImageUri', '');
    setLogoImageValidationError('');
  }, [updateGameField]);

  const handleFirstTeamImageDelete = useCallback(() => {
    updateGameField('firstTeamImageFile', null);
    updateGameField('firstTeamImageUri', '');
    setTeamImageValidationError('');
  }, [updateGameField]);

  const handleSecondTeamImageDelete = useCallback(() => {
    updateGameField('secondTeamImageFile', null);
    updateGameField('secondTeamImageUri', '');
    setSecondTeamImageValidationError('');
  }, [updateGameField]);

  const handleBackgroundMenuUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundMenu',
        setBackgroundMenuProgress,
        setBackgroundMenuValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundMenuDelete = useCallback(() => {
    updateGameField('backgroundMenuFile', null);
    updateGameField('backgroundMenuUri', '');
    setBackgroundMenuValidationError('');
  }, [updateGameField]);

  const handleBackgroundSelectTeamUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundSelectTeam',
        setBackgroundSelectTeamProgress,
        setBackgroundSelectTeamValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundSelectTeamDelete = useCallback(() => {
    updateGameField('backgroundSelectTeamFile', null);
    updateGameField('backgroundSelectTeamUri', '');
    setBackgroundSelectTeamValidationError('');
  }, [updateGameField]);

  const handleBackgroundImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundImage',
        setBackgroundImageProgress,
        setBackgroundImageValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundImageDelete = useCallback(() => {
    updateGameField('backgroundImageFile', null);
    updateGameField('backgroundImageUri', '');
    setBackgroundImageValidationError('');
  }, [updateGameField]);

  const handleBackgroundNextLevelUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundNextLevel',
        setBackgroundNextLevelProgress,
        setBackgroundNextLevelValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundNextLevelDelete = useCallback(() => {
    updateGameField('backgroundNextLevelFile', null);
    updateGameField('backgroundNextLevelUri', '');
    setBackgroundNextLevelValidationError('');
  }, [updateGameField]);

  const handleMissImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'missImage',
        setMissImageProgress,
        setMissImageValidationError
      ),
    [handleImageUpload]
  );

  const handleMissImageDelete = useCallback(() => {
    updateGameField('missImageFile', null);
    updateGameField('missImageUri', '');
    setMissImageValidationError('');
  }, [updateGameField]);

  const handleOutImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'outImage',
        setOutImageProgress,
        setOutImageValidationError
      ),
    [handleImageUpload]
  );

  const handleOutImageDelete = useCallback(() => {
    updateGameField('outImageFile', null);
    updateGameField('outImageUri', '');
    setOutImageValidationError('');
  }, [updateGameField]);

  const handleGameOverImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'gameOverImage',
        setGameOverImageProgress,
        setGameOverImageValidationError
      ),
    [handleImageUpload]
  );

  const handleGameOverImageDelete = useCallback(() => {
    updateGameField('gameOverImageFile', null);
    updateGameField('gameOverImageUri', '');
    setGameOverImageValidationError('');
  }, [updateGameField]);

  const handleGoalImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'goalImage',
        setGoalImageProgress,
        setGoalImageValidationError
      ),
    [handleImageUpload]
  );

  const handleGoalImageDelete = useCallback(() => {
    updateGameField('goalImageFile', null);
    updateGameField('goalImageUri', '');
    setGoalImageValidationError('');
  }, [updateGameField]);

  const handleHighBarImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'highBarImage',
        setHighBarImageProgress,
        setHighBarImageValidationError
      ),
    [handleImageUpload]
  );

  const handleHighBarImageDelete = useCallback(() => {
    updateGameField('highBarImageFile', null);
    updateGameField('highBarImageUri', '');
    setHighBarImageValidationError('');
  }, [updateGameField]);

  const handleRightBarImageUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'rightBarImage',
        setRightBarImageProgress,
        setRightBarImageValidationError
      ),
    [handleImageUpload]
  );

  const handleRightBarImageDelete = useCallback(() => {
    updateGameField('rightBarImageFile', null);
    updateGameField('rightBarImageUri', '');
    setRightBarImageValidationError('');
  }, [updateGameField]);

  const handleBackgroundVSUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundVS',
        setBackgroundVSProgress,
        setBackgroundVSValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundVSDelete = useCallback(() => {
    updateGameField('backgroundVSFile', null);
    updateGameField('backgroundVSUri', '');
    setBackgroundVSValidationError('');
  }, [updateGameField]);

  const handleBackgroundWaitingUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundWaiting',
        setBackgroundWaitingProgress,
        setBackgroundWaitingValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundWaitingDelete = useCallback(() => {
    updateGameField('backgroundWaitingFile', null);
    updateGameField('backgroundWaitingUri', '');
    setBackgroundWaitingValidationError('');
  }, [updateGameField]);

  const handleBackgroundExitUpload = useCallback(
    (files) =>
      handleImageUpload(
        files,
        'backgroundExit',
        setBackgroundExitProgress,
        setBackgroundExitValidationError
      ),
    [handleImageUpload]
  );

  const handleBackgroundExitDelete = useCallback(() => {
    updateGameField('backgroundExitFile', null);
    updateGameField('backgroundExitUri', '');
    setBackgroundExitValidationError('');
  }, [updateGameField]);

  // File error handlers for onFileError callback from FileUploadV2
  const handleImageFileError = useCallback((setValidationError) => {
    setValidationError(IMAGE_FILE_ERROR_DEFAULT);
  }, []);

  const handleGameImageFileError = useCallback(
    () => handleImageFileError(setGameImageValidationError),
    [handleImageFileError]
  );
  const handleLogoImageFileError = useCallback(
    () => handleImageFileError(setLogoImageValidationError),
    [handleImageFileError]
  );
  const handleFirstTeamImageFileError = useCallback(
    () => handleImageFileError(setTeamImageValidationError),
    [handleImageFileError]
  );
  const handleSecondTeamImageFileError = useCallback(
    () => handleImageFileError(setSecondTeamImageValidationError),
    [handleImageFileError]
  );
  const handleBackgroundMenuFileError = useCallback(
    () => handleImageFileError(setBackgroundMenuValidationError),
    [handleImageFileError]
  );
  const handleBackgroundSelectTeamFileError = useCallback(
    () => handleImageFileError(setBackgroundSelectTeamValidationError),
    [handleImageFileError]
  );
  const handleBackgroundImageFileError = useCallback(
    () => handleImageFileError(setBackgroundImageValidationError),
    [handleImageFileError]
  );
  const handleBackgroundNextLevelFileError = useCallback(
    () => handleImageFileError(setBackgroundNextLevelValidationError),
    [handleImageFileError]
  );
  const handleMissImageFileError = useCallback(
    () => handleImageFileError(setMissImageValidationError),
    [handleImageFileError]
  );
  const handleOutImageFileError = useCallback(
    () => handleImageFileError(setOutImageValidationError),
    [handleImageFileError]
  );
  const handleGameOverImageFileError = useCallback(
    () => handleImageFileError(setGameOverImageValidationError),
    [handleImageFileError]
  );
  const handleGoalImageFileError = useCallback(
    () => handleImageFileError(setGoalImageValidationError),
    [handleImageFileError]
  );
  const handleHighBarImageFileError = useCallback(
    () => handleImageFileError(setHighBarImageValidationError),
    [handleImageFileError]
  );
  const handleRightBarImageFileError = useCallback(
    () => handleImageFileError(setRightBarImageValidationError),
    [handleImageFileError]
  );
  const handleBackgroundVSFileError = useCallback(
    () => handleImageFileError(setBackgroundVSValidationError),
    [handleImageFileError]
  );
  const handleBackgroundWaitingFileError = useCallback(
    () => handleImageFileError(setBackgroundWaitingValidationError),
    [handleImageFileError]
  );
  const handleBackgroundExitFileError = useCallback(
    () => handleImageFileError(setBackgroundExitValidationError),
    [handleImageFileError]
  );

  const isLangValid = useCallback(
    (langCode) => {
      const data = langData[langCode];
      if (!data) return false;
      return data.title.trim() !== '' && data.tagDescription.trim() !== '';
    },
    [langData]
  );

  const isFormValid = useMemo(() => {
    const allLangsValid = languages.every((lang) => isLangValid(lang.code));
    const hasGameImage =
      gameData.gameImageFile !== null || gameData.gameImageUri !== '';
    const hasLogoImage =
      gameData.logoImageFile !== null || gameData.logoImageUri !== '';

    if (isBackgammonActivityType(formData?.activityType)) {
      return allLangsValid && hasGameImage && hasLogoImage;
    }

    const hasTeamNames =
      gameData.firstTeamName.trim() !== '' &&
      gameData.secondTeamName.trim() !== '';
    const hasFirstTeamImage =
      gameData.firstTeamImageFile !== null || gameData.firstTeamImageUri !== '';
    const hasSecondTeamImage =
      gameData.secondTeamImageFile !== null ||
      gameData.secondTeamImageUri !== '';

    return (
      allLangsValid &&
      hasGameImage &&
      hasLogoImage &&
      hasTeamNames &&
      hasFirstTeamImage &&
      hasSecondTeamImage
    );
  }, [languages, isLangValid, gameData, formData?.activityType]);

  // Sync display data to Redux using the new API payload structure:
  //   displayConfig.asset (images) + flat translations per language
  useEffect(() => {
    if (
      !isFreeKickActivityType(formData?.activityType) &&
      !isBackgammonActivityType(formData?.activityType)
    ) {
      return;
    }
    // Build flat per-language translations including team names + tutorial lines
    const translations = {};
    languages.forEach((lang) => {
      const data = langData[lang.code];
      if (data) {
        translations[lang.code] = {
          title: data.title || '',
          tagDescription: data.tagDescription || '',
          gameDescription: data.gameDescription || '',
          howToPlayRule: data.howToPlayRule || '',
          firstTeamName: gameData.firstTeamName || '',
          secondTeamName: gameData.secondTeamName || '',
          descriptionLine1: data.tutorialLine1 || '',
          descriptionLine2: data.tutorialLine2 || ''
        };
      }
    });

    // Preserve any existing asset fields (additional / background images that
    // aren't yet captured in this form) and overwrite the 4 fields we manage.
    const existingAssetSnapshot =
      formData?.displayConfig?.asset &&
      typeof formData.displayConfig.asset === 'object'
        ? formData.displayConfig.asset
        : {};

    const asset = {
      ...existingAssetSnapshot,
      gameImage: gameData.gameImageUri || '',
      logoImage: gameData.logoImageUri || '',
      firstTeamImage: gameData.firstTeamImageUri || '',
      secondTeamImage: gameData.secondTeamImageUri || '',
      backgroundMenuImage: gameData.backgroundMenuUri || '',
      backgroundSelectTeamImage: gameData.backgroundSelectTeamUri || '',
      backgroundImage: gameData.backgroundImageUri || '',
      backgroundNextLevelImage: gameData.backgroundNextLevelUri || '',
      missImage: gameData.missImageUri || '',
      outImage: gameData.outImageUri || '',
      gameOverImage: gameData.gameOverImageUri || '',
      goalImage: gameData.goalImageUri || '',
      highBarImage: gameData.highBarImageUri || '',
      rightBarImage: gameData.rightBarImageUri || '',
      backgroundVS: gameData.backgroundVSUri || '',
      backgroundWaiting: gameData.backgroundWaitingUri || '',
      backgroundExit: gameData.backgroundExitUri || ''
    };

    const existingDisplayConfig =
      formData?.displayConfig && typeof formData.displayConfig === 'object'
        ? formData.displayConfig
        : {};

    const displayConfig = {
      ...existingDisplayConfig,
      asset
    };

    // Merge with existing formData to preserve all fields including leaderboards
    dispatch(
      setActivityFormData({
        ...formData,
        displayConfig,
        translations
      })
    );
    // formData reference is intentionally omitted to avoid loops; we snapshot
    // it via the closure on every dispatch (langData / gameData drive updates).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langData, gameData, languages, dispatch]);

  const handleCancel = () => setIsCancel(true);

  const onConfirmCancel = () => {
    dispatch(resetActivityData({activityType: '', hasEndDate: false}));
    navigate(ROUTES.ACTIVITIES);
  };

  const onDismissCancel = () => setIsCancel(false);

  return {
    languages,
    activeTabIndex,
    setActiveTabIndex,
    tutorialTabIndex,
    setTutorialTabIndex,
    currentLangData,
    tutorialLangData,
    updateLangField,
    updateTutorialField,
    gameData,
    updateGameField,
    isFormValid,
    handleGameImageUpload,
    handleGameImageDelete,
    handleLogoImageUpload,
    handleLogoImageDelete,
    handleFirstTeamImageUpload,
    handleFirstTeamImageDelete,
    handleSecondTeamImageUpload,
    handleSecondTeamImageDelete,
    handleBackgroundMenuUpload,
    handleBackgroundMenuDelete,
    handleBackgroundSelectTeamUpload,
    handleBackgroundSelectTeamDelete,
    handleBackgroundImageUpload,
    handleBackgroundImageDelete,
    handleBackgroundNextLevelUpload,
    handleBackgroundNextLevelDelete,
    handleMissImageUpload,
    handleMissImageDelete,
    handleOutImageUpload,
    handleOutImageDelete,
    handleGameOverImageUpload,
    handleGameOverImageDelete,
    handleGoalImageUpload,
    handleGoalImageDelete,
    handleHighBarImageUpload,
    handleHighBarImageDelete,
    handleRightBarImageUpload,
    handleRightBarImageDelete,
    gameImageProgress,
    logoImageProgress,
    firstTeamImageProgress,
    secondTeamImageProgress,
    backgroundMenuProgress,
    backgroundSelectTeamProgress,
    backgroundImageProgress,
    backgroundNextLevelProgress,
    missImageProgress,
    outImageProgress,
    gameOverImageProgress,
    goalImageProgress,
    highBarImageProgress,
    rightBarImageProgress,
    gameImageValidationError,
    logoImageValidationError,
    teamImageValidationError,
    secondTeamImageValidationError,
    backgroundMenuValidationError,
    backgroundSelectTeamValidationError,
    backgroundImageValidationError,
    backgroundNextLevelValidationError,
    missImageValidationError,
    outImageValidationError,
    gameOverImageValidationError,
    goalImageValidationError,
    highBarImageValidationError,
    rightBarImageValidationError,
    handleGameImageFileError,
    handleLogoImageFileError,
    handleFirstTeamImageFileError,
    handleSecondTeamImageFileError,
    handleBackgroundMenuFileError,
    handleBackgroundSelectTeamFileError,
    handleBackgroundImageFileError,
    handleBackgroundNextLevelFileError,
    handleMissImageFileError,
    handleOutImageFileError,
    handleGameOverImageFileError,
    handleGoalImageFileError,
    handleHighBarImageFileError,
    handleRightBarImageFileError,
    handleBackgroundVSUpload,
    handleBackgroundVSDelete,
    handleBackgroundWaitingUpload,
    handleBackgroundWaitingDelete,
    handleBackgroundExitUpload,
    handleBackgroundExitDelete,
    backgroundVSProgress,
    backgroundWaitingProgress,
    backgroundExitProgress,
    backgroundVSValidationError,
    backgroundWaitingValidationError,
    backgroundExitValidationError,
    handleBackgroundVSFileError,
    handleBackgroundWaitingFileError,
    handleBackgroundExitFileError,
    handleCancel,
    onConfirmCancel,
    onDismissCancel,
    isCancel,
    showGuidelines,
    setShowGuidelines,
    previousGames,
    selectedPreviousGame,
    loadingPreviousGames,
    handlePreviousGameSelect,
    formData
  };
};

export default useFreeKickGameDisplay;
